// Segmentation.js
// OPTIMIZATION START - Canvas pooling and frame caching
import { getQualityProfile } from '../config/QualityProfiles.js';
import { deviceCapabilities } from '../utils/DeviceDetection.js';

class SegmentationProcessor {
    constructor() {
        // Use quality profile for adaptive resolution, with fallback to original values
        const quality = getQualityProfile?.() || {
            segmentation: { width: 512, height: 288 }
        };

        this.PROCESSING_WIDTH = quality.segmentation.width;
        this.PROCESSING_HEIGHT = quality.segmentation.height;

        // Parameters for soft thresholding
        this.threshold = 0.4; // Confidence cutoff for segmentation (increased from 0.2)
        this.softness = 0.1;  // Range around the threshold for gradual blending (increased from 0.07)

        // Performance optimization flags for low-end devices
        this.isMaliGPU = /Mali-G\d+/i.test(deviceCapabilities?.gpu?.renderer || '');
        this.enableBlur = !this.isMaliGPU; // Disable blur on Mali GPUs for performance
        this.enableTemporalSmoothing = !this.isMaliGPU; // Disable temporal smoothing on Mali GPUs

        console.log('[Segmentation] Performance mode:', {
            isMaliGPU: this.isMaliGPU,
            enableBlur: this.enableBlur,
            enableTemporalSmoothing: this.enableTemporalSmoothing,
            resolution: `${this.PROCESSING_WIDTH}x${this.PROCESSING_HEIGHT}`
        });

        this.lastMask = null;

        // OPTIMIZATION: Cache for last rendered frame to avoid flicker when skipping frames
        this.lastRenderedFrame = null;

        // OPTIMIZATION: Canvas pooling for memory efficiency
        this.canvasPool = [];
        this.maxPoolSize = 3;

        this.processingCanvas = document.createElement('canvas');
        this.processingCanvas.width = this.PROCESSING_WIDTH;
        this.processingCanvas.height = this.PROCESSING_HEIGHT;
        this.processingCtx = this.processingCanvas.getContext('2d', {
            willReadFrequently: true
        });
    }

    // OPTIMIZATION: Canvas pooling methods
    getCanvas() {
        if (this.canvasPool.length > 0) {
            return this.canvasPool.pop();
        }
        return this.createCanvas();
    }

    returnCanvas(canvas) {
        if (this.canvasPool.length < this.maxPoolSize) {
            // Clear canvas before returning to pool
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
            }
            this.canvasPool.push(canvas);
        }
    }

    createCanvas() {
        const canvas = document.createElement('canvas');
        canvas.width = this.PROCESSING_WIDTH;
        canvas.height = this.PROCESSING_HEIGHT;
        return canvas;
    }
    // OPTIMIZATION END

    processSegmentation(results, ctx, video, width, height) {
        const cropMargin = 0.02;
        let x_video = width * cropMargin;
        let y_video = height * cropMargin;
        let w_video = width * (1 - 2 * cropMargin);
        let h_video = height * (1 - 2 * cropMargin);


        if (!results?.categoryMask || !ctx) return;

        ctx.save();
        ctx.clearRect(0, 0, width, height);

        // Draw background image or color first (before drawing the video)
        ctx.fillStyle = "rgba(0, 0, 0, 0)"; // Transparent background
        ctx.fillRect(0, 0, width, height);

        if (video.readyState >= 2) {
            ctx.drawImage(
            video,             
            x_video,
            y_video,
            w_video,
            h_video,
            0,
            0,
            width,
            height);
        }

        const imageData = this.processingCtx.createImageData(
            this.PROCESSING_WIDTH,
            this.PROCESSING_HEIGHT
        );

        const maskArray = results.categoryMask.getAsFloat32Array();

        const scaleX = this.PROCESSING_WIDTH / results.categoryMask.width;
        const scaleY = this.PROCESSING_HEIGHT / results.categoryMask.height;
        const data = imageData.data;

        for (let y = 0; y < this.PROCESSING_HEIGHT; y++) {
            for (let x = 0; x < this.PROCESSING_WIDTH; x++) {
                const maskX = Math.floor(x / scaleX);
                const maskY = Math.floor(y / scaleY);
                const maskIndex = maskY * results.categoryMask.width + maskX;
                const category = maskArray[maskIndex];
                const pixelIndex = (y * this.PROCESSING_WIDTH + x) * 4;

                let alpha;
                if (category < this.threshold - this.softness) {
                    alpha = 0;
                } else if (category > this.threshold + this.softness) {
                    alpha = 255;
                } else {
                    alpha =
                        255 *
                        ((category - (this.threshold - this.softness)) / (2 * this.softness));
                }

                data[pixelIndex] = 255;
                data[pixelIndex + 1] = 255;
                data[pixelIndex + 2] = 255;
                data[pixelIndex + 3] = alpha;
            }
        }

        this.processingCtx.putImageData(imageData, 0, 0);

        // OPTIMIZATION: Conditional temporal smoothing (disabled on Mali GPU for performance)
        if (this.enableTemporalSmoothing) {
            if (this.lastMask) {
                this.processingCtx.globalAlpha = 0.35;
                this.processingCtx.drawImage(this.lastMask, 0, 0);
                this.processingCtx.globalAlpha = 1.0;
            }

            if (!this.lastMask) {
                this.lastMask = document.createElement('canvas');
                this.lastMask.width = this.PROCESSING_WIDTH;
                this.lastMask.height = this.PROCESSING_HEIGHT;
            }
            const lastMaskCtx = this.lastMask.getContext('2d');
            if (lastMaskCtx) {
                lastMaskCtx.clearRect(0, 0, this.PROCESSING_WIDTH, this.PROCESSING_HEIGHT);
                lastMaskCtx.drawImage(this.processingCanvas, 0, 0);
            }
        }

        // OPTIMIZATION: Conditional blur filter (disabled on Mali GPU for performance)
        let finalCanvas = this.processingCanvas;

        if (this.enableBlur) {
            // Use canvas from pool instead of creating new one (memory optimization)
            const blurredCanvas = this.getCanvas();
            const blurredCtx = blurredCanvas.getContext('2d');

            blurredCtx.filter = 'blur(2px)';
            blurredCtx.drawImage(this.processingCanvas, 0, 0);
            blurredCtx.filter = 'none';

            finalCanvas = blurredCanvas;
        }

        // --- Final Composition ---
        ctx.globalCompositeOperation = 'destination-out';

        let x = this.PROCESSING_WIDTH * cropMargin;
        let y = this.PROCESSING_HEIGHT * cropMargin;
        let w = this.PROCESSING_WIDTH * (1 - 2 * cropMargin);
        let h = this.PROCESSING_HEIGHT * (1 - 2 * cropMargin);


        ctx.drawImage(
            finalCanvas,
            x,
            y,
            w,
            h,
            0,
            0,
            width,
            height
        );

        ctx.restore();

        // OPTIMIZATION: Cache the current frame for reuse when skipping segmentation
        if (!this.lastRenderedFrame) {
            this.lastRenderedFrame = document.createElement('canvas');
            this.lastRenderedFrame.width = width;
            this.lastRenderedFrame.height = height;
        }
        const cacheCtx = this.lastRenderedFrame.getContext('2d');
        if (cacheCtx) {
            cacheCtx.clearRect(0, 0, width, height);
            cacheCtx.drawImage(ctx.canvas, 0, 0);
        }

        // OPTIMIZATION: Return canvas to pool for reuse (only if blur was used)
        if (this.enableBlur && finalCanvas !== this.processingCanvas) {
            this.returnCanvas(finalCanvas);
        }
    }

    // OPTIMIZATION: Redraw the last segmented frame (used when skipping segmentation processing)
    redrawLastFrame(ctx, video, width, height) {
        if (this.lastRenderedFrame) {
            // Reuse the cached segmented frame
            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(this.lastRenderedFrame, 0, 0);
        } else {
            // No cache yet, just draw the video
            ctx.clearRect(0, 0, width, height);
            if (video.readyState >= video.HAVE_CURRENT_DATA) {
                ctx.drawImage(video, 0, 0, width, height);
            }
        }
    }

    // CRITICAL: Cleanup method to release canvas memory and prevent WebView crashes
    cleanup() {
        console.log('[Segmentation] Cleaning up canvases and masks');

        // Clear canvas pool
        this.canvasPool.forEach(canvas => {
            try {
                canvas.width = 0;
                canvas.height = 0;
            } catch (e) {
                // Ignore errors during cleanup
            }
        });
        this.canvasPool = [];

        // Clear lastMask canvas (but don't null the reference to avoid race conditions)
        if (this.lastMask) {
            try {
                this.lastMask.width = 0;
                this.lastMask.height = 0;
            } catch (e) {
                // Ignore errors during cleanup
            }
            this.lastMask = null;
        }

        // Clear lastRenderedFrame canvas
        if (this.lastRenderedFrame) {
            try {
                this.lastRenderedFrame.width = 0;
                this.lastRenderedFrame.height = 0;
            } catch (e) {
                // Ignore errors during cleanup
            }
            this.lastRenderedFrame = null;
        }

        // IMPORTANT: DON'T null out processingCanvas or processingCtx
        // They might still be in use by camera frames being processed
        // Just reset dimensions to free memory
        if (this.processingCanvas) {
            try {
                this.processingCanvas.width = 1;
                this.processingCanvas.height = 1;
            } catch (e) {
                // Ignore errors during cleanup
            }
        }

        console.log('[Segmentation] Cleanup complete');
    }
}

export default SegmentationProcessor;